import { collection, doc, limit, onSnapshot, query, updateDoc } from 'firebase/firestore'
import { getDbOrThrow } from '@/lib/firebase'
import { mapUserProfile } from '@/lib/mappers'
import type { UserPlan, UserProfile, UserRole } from '@/lib/types'

/**
 * Cap on the number of profiles fetched at once. The rules allow an admin to list the whole
 * collection, but an unbounded live listener would grow without limit; searching and sorting happen
 * on the client, which is fine at this size. Replace with `startAfter()` paging when the collection
 * outgrows this.
 */
export const USERS_PAGE_LIMIT = 500

/**
 * Live list of `users`. A listener (not a one-off read) so a role change — from here, from another
 * admin or from the Firebase console — is reflected immediately.
 *
 * No `orderBy`: sorting happens on the client, so no Firestore index is needed and documents with a
 * missing field are not silently dropped from the result.
 */
export function subscribeToUsers(
  onData: (users: UserProfile[]) => void,
  onError: (error: Error) => void,
): () => void {
  const usersQuery = query(collection(getDbOrThrow(), 'users'), limit(USERS_PAGE_LIMIT))
  return onSnapshot(
    usersQuery,
    (snapshot) => onData(snapshot.docs.map((item) => mapUserProfile(item.id, item.data()))),
    onError,
  )
}

/**
 * Change someone's role, plan and/or active flag. The write carries ONLY these keys: the security
 * rules reject an update whose `affectedKeys()` contains anything else, so the patch is built from
 * the fields that actually changed and nothing is copied along "just in case".
 */
export function updateUserAccess(
  uid: string,
  patch: { role?: UserRole; plan?: UserPlan; isActive?: boolean },
): Promise<void> {
  if (Object.keys(patch).length === 0) return Promise.resolve()
  return updateDoc(doc(getDbOrThrow(), 'users', uid), { ...patch })
}
